import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings, Save, Loader2, Mail, Phone, Globe, User, MapPin, Clock, Plus, X } from 'lucide-react';
import clsx from 'clsx';
import { useAuthStore } from '../../stores/authStore';
import { useSiteSettings, useUpdateSiteSettings } from '../../services/settingsService';

/**
 * Admin settings page — persisted to Supabase site_settings table.
 */
export default function AdminSettingsPage() {
  const { user } = useAuthStore();
  const { data: settings, isLoading } = useSiteSettings();
  const updateMutation = useUpdateSiteSettings();
  const [saved, setSaved] = useState(false);

  const [form, setForm] = useState({
    restaurant_name: '',
    tagline: '',
    whatsapp_phone: '',
    phone: '',
    email: '',
    address: '',
    opening_hours: '',
    additional_phones: [],
  });

  // Populate form when settings load
  useEffect(() => {
    if (settings) {
      setForm({
        restaurant_name: settings.restaurant_name || '',
        tagline: settings.tagline || '',
        whatsapp_phone: settings.whatsapp_phone || '',
        phone: settings.phone || '',
        email: settings.email || '',
        address: settings.address || '',
        opening_hours: settings.opening_hours || '',
        additional_phones: settings.additional_phones || [],
      });
    }
  }, [settings]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const addPhone = () => {
    setForm((prev) => ({
      ...prev,
      additional_phones: [...prev.additional_phones, { label: '', number: '' }],
    }));
  };

  const updatePhone = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      additional_phones: prev.additional_phones.map((phone, i) =>
        i === index ? { ...phone, [field]: value } : phone
      ),
    }));
  };

  const removePhone = (index) => {
    setForm((prev) => ({
      ...prev,
      additional_phones: prev.additional_phones.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await updateMutation.mutateAsync({
        restaurant_name: form.restaurant_name.trim(),
        tagline: form.tagline.trim(),
        whatsapp_phone: form.whatsapp_phone.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        address: form.address.trim(),
        opening_hours: form.opening_hours.trim(),
        additional_phones: form.additional_phones.filter(p => p.label && p.number),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      // mutation error handled by react-query
    }
  };

  const inputClass =
    'w-full pl-10 pr-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted';

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <div className="h-8 w-32 bg-brand-light rounded animate-pulse" />
          <div className="h-4 w-64 bg-brand-light rounded animate-pulse mt-2" />
        </div>
        {[1, 2].map((n) => (
          <div key={n} className="bg-brand-white border border-ui-border rounded-2xl p-6 h-40 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold text-brand-dark">Settings</h1>
        <p className="text-sm text-brand-gray mt-1">Manage your restaurant and account settings</p>
      </div>

      {saved && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-3 bg-green-50 border border-success/20 rounded-xl text-sm text-success font-medium"
        >
          Settings saved successfully!
        </motion.div>
      )}

      {updateMutation.isError && (
        <div className="p-3 bg-red-50 border border-error/20 rounded-xl text-sm text-error font-medium">
          Failed to save: {updateMutation.error?.message || 'Unknown error'}
        </div>
      )}

      {/* Account info */}
      <div className="bg-brand-white border border-ui-border rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 bg-brand-red/10 rounded-xl flex items-center justify-center">
            <User size={20} className="text-brand-red" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-brand-dark">Account</h2>
            <p className="text-xs text-brand-gray">Owner identity</p>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-brand-dark mb-1.5">Owner Email</label>
            <div className="relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-muted" />
              <input
                type="email"
                disabled
                value={user?.email || ''}
                className="w-full pl-10 pr-4 py-2.5 text-sm bg-brand-light border border-ui-border rounded-xl text-brand-gray cursor-not-allowed"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Restaurant settings */}
      <form onSubmit={handleSave} className="bg-brand-white border border-ui-border rounded-2xl p-6 space-y-5">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
            <Settings size={20} className="text-amber-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-brand-dark">Restaurant</h2>
            <p className="text-xs text-brand-gray">Business configuration</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-brand-dark mb-1.5">Restaurant Name</label>
          <div className="relative">
            <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-muted" />
            <input
              type="text"
              value={form.restaurant_name}
              onChange={(e) => updateField('restaurant_name', e.target.value)}
              placeholder="Restaurant name"
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-brand-dark mb-1.5">Tagline</label>
          <div className="relative">
            <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-muted" />
            <input
              type="text"
              value={form.tagline}
              onChange={(e) => updateField('tagline', e.target.value)}
              placeholder="Short tagline"
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-brand-dark mb-1.5">Phone Number</label>
          <div className="relative">
            <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-muted" />
            <input
              type="tel"
              value={form.whatsapp_phone}
              onChange={(e) => updateField('whatsapp_phone', e.target.value)}
              placeholder="254700000000"
              className={inputClass}
            />
          </div>
          <p className="text-xs text-brand-gray mt-1">Include country code, no + symbol. Used for Talk to us & enquiries.</p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-brand-dark mb-1.5">Email (optional)</label>
          <div className="relative">
            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-muted" />
            <input
              type="email"
              value={form.email}
              onChange={(e) => updateField('email', e.target.value)}
              placeholder="contact@restaurant.com"
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-brand-dark mb-1.5">Address / Location</label>
          <div className="relative">
            <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-muted" />
            <input
              type="text"
              value={form.address}
              onChange={(e) => updateField('address', e.target.value)}
              placeholder="Nairobi, Kenya"
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-brand-dark mb-1.5">Opening Hours</label>
          <div className="relative">
            <Clock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-muted" />
            <input
              type="text"
              value={form.opening_hours}
              onChange={(e) => updateField('opening_hours', e.target.value)}
              placeholder="Mon – Sun, 10am – 10pm"
              className={inputClass}
            />
          </div>
        </div>

        {/* Additional Phone Numbers */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="block text-sm font-semibold text-brand-dark">
              Additional Phone Numbers
            </label>
            <button
              type="button"
              onClick={addPhone}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-brand-red bg-brand-red/10 hover:bg-brand-red/20 rounded-lg transition-colors"
            >
              <Plus size={14} />
              Add Phone
            </button>
          </div>
          
          <AnimatePresence>
            {form.additional_phones.map((phone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-3"
              >
                <div className="flex gap-2 items-start p-3 bg-brand-light rounded-xl">
                  <div className="flex-1 space-y-2">
                    <input
                      type="text"
                      value={phone.label}
                      onChange={(e) => updatePhone(index, 'label', e.target.value)}
                      placeholder="Label (e.g., Branch 1, Support)"
                      className="w-full px-3 py-2 text-sm bg-brand-white border border-ui-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                    />
                    <input
                      type="tel"
                      value={phone.number}
                      onChange={(e) => updatePhone(index, 'number', e.target.value)}
                      placeholder="254700000000"
                      className="w-full px-3 py-2 text-sm bg-brand-white border border-ui-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removePhone(index)}
                    className="p-2 text-error hover:bg-error/10 rounded-lg transition-colors flex-shrink-0"
                  >
                    <X size={16} />
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          
          {form.additional_phones.length === 0 && (
            <p className="text-xs text-brand-gray italic">
              No additional phone numbers. Click "Add Phone" to add more contact numbers.
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={updateMutation.isPending}
          className={clsx(
            'flex items-center gap-2 px-6 py-2.5 text-sm font-bold rounded-xl transition-all',
            updateMutation.isPending
              ? 'bg-brand-red/60 text-brand-white cursor-not-allowed'
              : 'bg-brand-red hover:bg-red-700 text-brand-white shadow-lg'
          )}
        >
          {updateMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
        </button>
      </form>
    </div>
  );
}
